import { z } from 'zod';

import { parseOrThrow } from '../../../api';
import type {
  AlwaysReachesKey,
  CoverageGap,
  CoverageNotice,
  FeedItem,
  GapDetail,
  GapPerson,
  IssueStartResult,
  IssueTakeOverResult,
  LandingRecord,
  MySettings,
  NotificationSignal,
  OnboardingState,
  PersonChannel,
  PlantPolicy,
  PlantRouting,
  PushPayload,
  PushSubscriptionSaved,
  QuietSummary,
  ToldAudience,
  ToldRole,
} from '../types/domain.types';

import {
  ALWAYS_REACHES_KEYS,
  CHANGE_NOTICES,
  COVERAGE_GAPS,
  DELIVERY_MODES,
  DIGEST_WHEN,
  FEED_STATES,
  GAP_REASONS,
  HEAR_LINES,
  HOOTER_STATES,
  ISSUE_STATUSES,
  LADDER_STEPS_UI,
  LANDING_LANGUAGES,
  MANAGEABLE_REASONS,
  NEXT_ACTIONS,
  ONBOARDING_ENTRIES,
  ONBOARDING_STATUSES,
  QUIET_HOURS_SOURCES,
  READING_TRENDS,
  REALTIME_CHANNELS,
  REGIONAL_LANGUAGES,
  ROLE_GROUPS,
  ROUTING_BUCKETS,
  SEVERITIES,
  TOLD_ROLES,
} from '../types/domain.types';

const SAFE_SCHEMES = ['http:', 'https:'];
const RELATIVE_BASE = 'https://relative.invalid';

const safeLink = (value: string | null): string | null => {
  if (value === null) return null;
  const trimmed = value.trim();
  if (trimmed === '') return null;
  let url: URL;
  try {
    url = new URL(trimmed, RELATIVE_BASE);
  } catch {
    return null;
  }
  if (!SAFE_SCHEMES.includes(url.protocol)) return null;
  if (trimmed.startsWith('/') && url.origin !== RELATIVE_BASE) return null;
  if (!trimmed.startsWith('/') && url.origin === RELATIVE_BASE) return null;
  return trimmed;
};

const safeInAppPath = (value: string | null): string | null => {
  const link = safeLink(value);
  return link !== null && link.startsWith('/') ? link : null;
};

const severity = z.enum(SEVERITIES);
const mode = z.enum(DELIVERY_MODES);
const minuteOfDay = z.number().int().min(0).max(1439);
const isoInstant = z.string().datetime({ offset: true });
const nullableText = z.string().nullable().default(null);
const textOrEmpty = z
  .string()
  .nullish()
  .transform((value) => value ?? '');

const MORNING_DIGEST_MINUTE = 6 * 60;

const modesSchema = z.object({ EMERGENCY: mode, MAJOR: mode, MINOR: mode, CAUTION: mode });

const allowedSchema = z.object({
  EMERGENCY: z.array(mode).default([]),
  MAJOR: z.array(mode).default([]),
  MINOR: z.array(mode).default([]),
  CAUTION: z.array(mode).default([]),
});

export const quietWindowSchema = z.object({ startMinute: minuteOfDay, endMinute: minuteOfDay });

const ALWAYS_REACHES: ReadonlySet<string> = new Set(ALWAYS_REACHES_KEYS);
const isAlwaysReachesKey = (key: string): key is AlwaysReachesKey => ALWAYS_REACHES.has(key);

const KNOWN_HEAR_LINES: ReadonlySet<string> = new Set(HEAR_LINES);
const isHearLine = (line: string): line is (typeof HEAR_LINES)[number] => KNOWN_HEAR_LINES.has(line);

const KNOWN_GAPS: ReadonlySet<string> = new Set(COVERAGE_GAPS);
const isCoverageGap = (gap: string): gap is CoverageGap => KNOWN_GAPS.has(gap);

const KNOWN_ROLE_GROUPS: ReadonlySet<string> = new Set(ROLE_GROUPS);
const isRoleGroup = (group: string): group is (typeof ROLE_GROUPS)[number] => KNOWN_ROLE_GROUPS.has(group);

const KNOWN_TOLD_ROLES: ReadonlySet<string> = new Set(TOLD_ROLES);
const isToldRole = (role: string): role is ToldRole => KNOWN_TOLD_ROLES.has(role);

export const onboardingSchema = z.object({
  status: z.enum(ONBOARDING_STATUSES),
  at: isoInstant.nullable(),
  entry: z.enum(ONBOARDING_ENTRIES),
});

const pushStateSchema = z.object({
  promptedAt: isoInstant.nullable(),
  devices: z.number().int().min(0),
});

const quietStateSchema = z.object({
  active: z.boolean(),
  until: isoInstant.nullable(),
  startsAt: isoInstant.nullable(),
});

const changeNotice = z.enum(CHANGE_NOTICES).optional().catch(undefined);

const noticesByModeSchema = z.object({ NOW: changeNotice, DIGEST: changeNotice, OFF: changeNotice }).catch({});

const changeNoticesSchema = z.object({
  EMERGENCY: noticesByModeSchema,
  MAJOR: noticesByModeSchema,
  MINOR: noticesByModeSchema,
  CAUTION: noticesByModeSchema,
});

const NO_CHANGE_NOTICES: z.infer<typeof changeNoticesSchema> = { EMERGENCY: {}, MAJOR: {}, MINOR: {}, CAUTION: {} };

const digestWhenSchema = z.object({
  EMERGENCY: z.enum(DIGEST_WHEN),
  MAJOR: z.enum(DIGEST_WHEN),
  MINOR: z.enum(DIGEST_WHEN),
  CAUTION: z.enum(DIGEST_WHEN),
});

const noOperatorPlantSchema = z.object({
  plantId: z.string(),
  plantName: z.string(),
});

const handoffStallSchema = z.object({
  handoffs: z.number().int().positive(),
  withinHours: z.number().finite().positive(),
});

export const mySettingsSchema = z.object({
  userId: z.string(),
  roleKey: z.string(),
  roleGroup: z
    .string()
    .nullable()
    .transform((group) => (group !== null && isRoleGroup(group) ? group : null)),
  isOperational: z.boolean(),
  receivesHandoffAlerts: z.boolean(),
  manageable: z.boolean(),
  modes: modesSchema,
  allowed: allowedSchema,
  quietHours: quietWindowSchema,
  quietHoursEnabled: z.boolean(),
  alwaysReaches: z.array(z.string()).transform((keys) => keys.filter(isAlwaysReachesKey)),
  phoneVerified: z.boolean(),
  emailVerified: z.boolean(),
  realtimeChannel: z.enum(REALTIME_CHANNELS),
  handoffAlerts: z.boolean(),
  handoffStall: handoffStallSchema.nullable().catch(null),
  onboarding: onboardingSchema,
  quietHoursSource: z.enum(QUIET_HOURS_SOURCES),
  push: pushStateSchema,
  digestWhen: digestWhenSchema,
  manageableReason: z.enum(MANAGEABLE_REASONS).nullable().catch(null),
  quiet: quietStateSchema,
  reachesOn: z.enum(REALTIME_CHANNELS).nullable(),
  switchTo: z.enum(REALTIME_CHANNELS).nullable(),
  hears: z
    .array(z.string())
    .transform((lines) => lines.filter(isHearLine))
    .catch([]),
  changeNotices: changeNoticesSchema.catch(NO_CHANGE_NOTICES),
  digestAtMinute: minuteOfDay.catch(MORNING_DIGEST_MINUTE),
  noOperatorAt: z.array(noOperatorPlantSchema).catch([]),
});

export type MySettingsDto = z.infer<typeof mySettingsSchema>;

const toldPersonSchema = z.object({
  userId: z.string(),
  name: z.string(),
  roles: z.array(z.string()).default([]).transform((roles) => roles.filter(isToldRole)),
});

type ToldEntry = string | z.infer<typeof toldPersonSchema>;

const nameOf = (entry: ToldEntry): string => (typeof entry === 'string' ? entry : entry.name);

const audienceOf = (entries: readonly ToldEntry[]): ToldAudience | null => {
  const people = entries.filter((entry): entry is z.infer<typeof toldPersonSchema> => typeof entry !== 'string');
  if (people.length !== entries.length) return null;
  return {
    leads: people.filter((person) => person.roles.includes('LEAD')).map(nameOf),
    siteAdmins: people.filter((person) => person.roles.includes('SITE_ADMIN')).map(nameOf),
  };
};

const coverageNoticeSchema = z
  .object({
    severity: z.enum(['MAJOR', 'MINOR']).nullable().catch(null),
    realtime: z.boolean(),
    told: z.array(z.union([z.string(), toldPersonSchema])).default([]),
    delivered: z.boolean(),
  })
  .transform(
    (notice): CoverageNotice => ({
      severity: notice.severity,
      realtime: notice.realtime,
      told: notice.told.map(nameOf),
      audience: audienceOf(notice.told),
      delivered: notice.delivered,
    }),
  );

export const settingsSavedSchema = mySettingsSchema.extend({ notified: coverageNoticeSchema.nullable() });

export const readingSchema = z.object({
  value: z.number().finite(),
  unit: textOrEmpty,
  band: nullableText,
  trend: z.enum(READING_TRENDS).nullable().default(null).catch(null),
});

export const startedBySchema = z.object({ userId: z.string(), name: z.string(), at: isoInstant });

const hooterStatusSchema = z.object({
  state: z.enum(HOOTER_STATES),
  calling: z.array(z.string()).default([]),
});

const optional = <S extends z.ZodTypeAny>(schema: S) => schema.nullable().default(null).catch(null);

const feedRowSchema = z.object({
  issueId: z.string().nullable().default(null),
  plantId: textOrEmpty,
  severity,
  step: z.enum(LADDER_STEPS_UI).catch('UNKNOWN'),
  title: textOrEmpty,
  at: isoInstant,
  state: z.enum(FEED_STATES),
  link: z.string().nullable().default(null).transform(safeLink),
  issueTitle: optional(z.string().min(1)),
  plantName: optional(z.string().min(1)),
  location: optional(z.string().min(1)),
  reading: optional(readingSchema),
  startedBy: optional(startedBySchema),
  status: optional(z.enum(ISSUE_STATUSES)),
  hooter: optional(hooterStatusSchema),
  fullScreenAlarm: optional(z.boolean()),
  nextAction: optional(z.enum(NEXT_ACTIONS)),
});

export const feedItemSchema = feedRowSchema.transform(
  (row): FeedItem => ({ ...row, fullScreenAlarm: row.fullScreenAlarm ?? row.severity === 'EMERGENCY' }),
);
export const feedSchema = z.array(feedItemSchema);
export type FeedItemDto = z.infer<typeof feedItemSchema>;

const quietSummarySchema = z.object({
  active: z.boolean(),
  until: isoInstant.nullable(),
  startsAt: isoInstant.nullable(),
  held: z.object({ MINOR: z.number().int().min(0), CAUTION: z.number().int().min(0) }),
});

const QUIET_UNKNOWN: z.infer<typeof quietSummarySchema> = { active: false, until: null, startsAt: null, held: { MINOR: 0, CAUTION: 0 } };

const feedBodySchema = z.object({ items: z.array(z.unknown()), quiet: quietSummarySchema.catch(QUIET_UNKNOWN) });

export interface ParsedFeed {
  readonly items: FeedItemDto[];
  readonly skipped: number;
  readonly quiet: QuietSummary;
}

export const parseFeed = (raw: unknown): ParsedFeed => {
  const body = parseOrThrow(feedBodySchema, raw, 'feed');
  const items: FeedItemDto[] = [];
  let skipped = 0;
  for (const row of body.items) {
    const parsed = feedItemSchema.safeParse(row);
    if (parsed.success) items.push(parsed.data);
    else skipped += 1;
  }
  return { items, skipped, quiet: body.quiet };
};

const REGIONAL: ReadonlySet<string> = new Set(REGIONAL_LANGUAGES);

const regionalLanguagesSchema = z
  .array(z.string())
  .transform((languages) => languages.filter((language): language is (typeof REGIONAL_LANGUAGES)[number] => REGIONAL.has(language)));

export const plantPolicySchema = z.object({
  plantId: z.string(),
  hooterEnabled: z.boolean(),
  hooterDurationSec: z.number().int(),
  quietHours: quietWindowSchema,
  quietHoursEnabled: z.boolean(),
  languages: regionalLanguagesSchema,
  defaultLanguage: z.enum(REGIONAL_LANGUAGES),
  supportedLanguages: regionalLanguagesSchema,
  hooterDurations: z.object({
    minSec: z.number().int().min(0),
    maxSec: z.number().int().min(0),
    presetsSec: z.array(z.number().int().min(0)),
  }),
});
export type PlantPolicyDto = z.infer<typeof plantPolicySchema>;

const routingPersonSchema = z.object({
  userId: z.string(),
  name: textOrEmpty,
  roleKey: textOrEmpty,
  bucket: z.enum(ROUTING_BUCKETS).nullable(),
  inAppOnly: z.boolean().default(false),
});

const severityRoutingSchema = z.object({
  severity,
  toldNow: z.array(routingPersonSchema).default([]),
  morningEmail: z.array(routingPersonSchema).default([]),
  morningEmailWhenOverdue: z.array(routingPersonSchema).default([]),
  callCount: z.number().int().default(0),
  hooterSeconds: z.number().nullable().default(null),
  fullScreenAlarm: z.boolean().default(false),
  overdueAfterMinutes: z.number().int().min(0).nullable().default(null).catch(null),
  realtimeExpected: z.boolean(),
});

const gapPersonSchema = z.object({
  userId: z.string(),
  name: textOrEmpty,
  roleGroup: z.enum(ROLE_GROUPS).nullable().catch(null),
  reason: z.enum(GAP_REASONS),
});

const gapDetailRowSchema = z.object({ gap: z.string(), people: z.array(z.unknown()).default([]) });

const gapPeopleOf = (people: readonly unknown[]): GapPerson[] =>
  people.flatMap((person): GapPerson[] => {
    const read = gapPersonSchema.safeParse(person);
    return read.success ? [read.data] : [];
  });

const gapDetailOf = (row: unknown): GapDetail[] => {
  const parsed = gapDetailRowSchema.safeParse(row);
  if (!parsed.success) return [];
  const { gap, people } = parsed.data;
  return isCoverageGap(gap) ? [{ gap, people: gapPeopleOf(people) }] : [];
};

const gapDetailsSchema = z
  .array(z.unknown())
  .default([])
  .transform((rows): GapDetail[] => rows.flatMap(gapDetailOf));

export const plantRoutingSchema = z.object({
  plantId: textOrEmpty,
  health: z.object({
    ok: z.boolean(),
    gaps: z.array(z.string()).default([]).transform((gaps) => gaps.filter(isCoverageGap)),
    blocksRosterSave: z.boolean().default(false),
    gapDetails: gapDetailsSchema,
  }),
  severities: z.array(severityRoutingSchema).default([]),
  quietHours: z.object({ usingDefault: z.number().int().default(0), total: z.number().int().default(0) }),
  failures: z.object({
    since: textOrEmpty,
    people: z.number().int().default(0),
    byPerson: z
      .array(
        z.object({
          userId: z.string(),
          name: textOrEmpty,
          channel: textOrEmpty,
          count: z.number().int().default(0),
          switchTo: z.enum(REALTIME_CHANNELS).nullable().catch(null),
        }),
      )
      .default([]),
  }),
});
export type PlantRoutingDto = z.infer<typeof plantRoutingSchema>;

export const settingsViolationsSchema = z.array(z.object({ severity, mode, allowed: z.array(mode) }));

const holding = {
  startedBy: startedBySchema.nullable(),
  status: z.enum(ISSUE_STATUSES).nullable(),
  nextAction: z.enum(NEXT_ACTIONS).nullable(),
};

export const issueStartSchema = z.discriminatedUnion('outcome', [
  z.object({ outcome: z.literal('STARTED'), hooterSilenced: z.boolean(), ...holding }),
  z.object({ outcome: z.literal('ALREADY_STARTED'), hooterSilenced: z.boolean(), ...holding }),
  z.object({ outcome: z.literal('NOT_FOUND') }),
]);

export const issueTakeOverSchema = z.discriminatedUnion('outcome', [
  z.object({ outcome: z.literal('TAKEN_OVER'), previousOwner: z.string().nullable(), ...holding }),
  z.object({ outcome: z.literal('ALREADY_YOURS'), ...holding }),
  z.object({ outcome: z.literal('NOT_STARTED') }),
  z.object({ outcome: z.literal('NOT_FOUND') }),
]);

const appPathSchema = z.string().nullable().default(null).transform(safeInAppPath);

export const landingRecordSchema = z.object({
  issueId: z.string(),
  severity,
  title: z.string(),
  plantName: z.string(),
  area: nullableText,
  equipment: nullableText,
  firedAt: isoInstant,
  reading: optional(readingSchema),
  status: z.enum(ISSUE_STATUSES),
  nextAction: z.enum(NEXT_ACTIONS).nullable(),
  startedBy: startedBySchema.nullable().default(null),
  resolved: optional(z.object({ at: isoInstant, byName: z.string() })),
  hooter: optional(z.object({ sounding: z.boolean(), callsPlaced: z.number().int().min(0).default(0) })),
  language: z.enum(LANDING_LANGUAGES).catch('en'),
  channel: z.enum(REALTIME_CHANNELS).catch('WHATSAPP'),
  recipient: z.object({ phoneMasked: z.string().trim().min(1) }),
  appPath: appPathSchema,
});

export const pushSubscriptionSavedSchema = z.object({ id: z.string(), createdAt: isoInstant });

export const pushPayloadSchema = z.object({
  v: z.literal(1),
  issueId: z
    .string()
    .nullish()
    .transform((issueId) => (issueId ? issueId : null)),
  plantId: z.string(),
  severity,
  step: z.enum(LADDER_STEPS_UI).catch('UNKNOWN'),
  at: isoInstant,
  link: z.string().nullable().default(null).transform(safeLink),
  lang: z.enum(LANDING_LANGUAGES).catch('en'),
  issueTitle: textOrEmpty,
  plantName: textOrEmpty,
  location: nullableText,
  reading: optional(readingSchema),
  startedBy: optional(startedBySchema),
  hooter: optional(hooterStatusSchema),
  text: z.object({ title: z.string(), body: z.string(), start: z.string(), open: z.string() }),
});

export const personChannelSchema = z.object({ userId: z.string(), realtimeChannel: z.enum(REALTIME_CHANNELS) });

export const notificationSignalSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('feed') }),
  z.object({ kind: z.literal('settings') }),
  z.object({ kind: z.literal('issue'), issueId: z.string().min(1) }),
]);

const jsonOf = (text: string): unknown => {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
};

export const parseSignal = (data: string): NotificationSignal | null => {
  const parsed = notificationSignalSchema.safeParse(jsonOf(data));
  return parsed.success ? parsed.data : null;
};

export { parseOrThrow };

type Produces<Parsed, Domain> = [Parsed] extends [Domain] ? ([keyof Domain] extends [keyof Parsed] ? true : false) : false;
const schemasMatchDomain: [
  Produces<z.infer<typeof mySettingsSchema>, MySettings>,
  Produces<z.infer<typeof onboardingSchema>, OnboardingState>,
  Produces<z.infer<typeof feedItemSchema>, FeedItem>,
  Produces<z.infer<typeof quietSummarySchema>, QuietSummary>,
  Produces<z.infer<typeof plantPolicySchema>, PlantPolicy>,
  Produces<z.infer<typeof plantRoutingSchema>, PlantRouting>,
  Produces<z.infer<typeof issueStartSchema>, IssueStartResult>,
  Produces<z.infer<typeof issueTakeOverSchema>, IssueTakeOverResult>,
  Produces<z.infer<typeof landingRecordSchema>, LandingRecord>,
  Produces<z.infer<typeof pushSubscriptionSavedSchema>, PushSubscriptionSaved>,
  Produces<z.infer<typeof pushPayloadSchema>, PushPayload>,
  Produces<z.infer<typeof personChannelSchema>, PersonChannel>,
  Produces<z.infer<typeof notificationSignalSchema>, NotificationSignal>,
] = [true, true, true, true, true, true, true, true, true, true, true, true, true];
void schemasMatchDomain;
